import { useCallback, useEffect, useRef, useState } from 'react';
import { distance, initialNetwork, networkLength, simulateConnections, targetSignature, type Coordinate, type Network, type NetworkTool } from './network';
import { useDensity } from './useDensity';
import { pipesInLasso } from './lasso';

const EMPTY_NETWORK: Network = { plants: [], pipes: [] };
/** Auteur fictif du calcul de tracé (aucun compte réel dans le prototype). */
const TRACE_AUTHOR = 'Claire Moreau';

export function useNetwork(selectionId: string, targets: Coordinate[], heat = 0, members = '', heatByTarget?: number[]) {
  const [networks, setNetworks] = useState<Record<string, Network>>(() => ({
    'demo-2': initialNetwork('demo-2'), 'demo-3': initialNetwork('demo-3'),
  }));
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const pipesVisible = visibility[selectionId] ?? true;
  const setPipesVisible = (visible: boolean) => setVisibility(prev => ({ ...prev, [selectionId]: visible }));
  const [activeTool, setActiveTool] = useState<NetworkTool | null>(null);
  const [draft, setDraft] = useState<Coordinate[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);
  const [traceLoading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // New selections (and « aucune sélection ») start without plants nor pipes.
  const network = networks[selectionId] ?? EMPTY_NETWORK;
  const demands = targets.length ? targets.map((coordinates, i) => ({
    coordinates, heat: heatByTarget?.[i] ?? heat / targets.length,
  })) : undefined;
  const density = useDensity(selectionId, network, heat, `${members}|${targetSignature(targets)}`, demands,
    () => setVisibility((prev) => ({ ...prev, [selectionId]: true })));
  const loading = traceLoading || density.loading;
  const cancelDensity = density.cancel;
  const cancel = useCallback(() => {
    cancelDensity();
    clearTimeout(timer.current);
    timer.current = undefined;
    setLoading(false); setActiveTool(null); setDraft([]); setRemoved([]); setSelectedPlantId(null); setMessage('');
  }, [cancelDensity]);
  useEffect(() => {
    cancel();
    return () => clearTimeout(timer.current);
  }, [selectionId, cancel]);
  useEffect(() => {
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [cancel]);
  const start = (tool: NetworkTool) => {
    if (activeTool || loading) return;
    if ((tool === 'move-plant' || tool === 'remove-plant') && !network.plants.length) return;
    setMessage(''); setDraft([]); setRemoved([]); setSelectedPlantId(null); setActiveTool(tool);
  };
  const pickPoint = (point: Coordinate) => {
    if (activeTool === 'plant' || (activeTool === 'move-plant' && selectedPlantId)) setDraft([point]);
    if (activeTool === 'draw-pipe') setDraft((prev) =>
      prev.length && distance(prev[prev.length - 1], point) < 0.1 ? prev : [...prev, point]);
  };
  const pickPlant = (id: string) => {
    if ((activeTool !== 'move-plant' && activeTool !== 'remove-plant') || !network.plants.some((p) => p.id === id)) return;
    setSelectedPlantId((prev) => prev === id ? null : id);
    setDraft([]);
  };
  const beginMove = (id: string) => {
    if (activeTool || loading || !network.plants.some((p) => p.id === id)) return false;
    setSelectedPlantId(id); setDraft([]); setRemoved([]); setMessage(''); setActiveTool('move-plant');
    return true;
  };
  const pickLasso = (polygon: Coordinate[], finished: boolean) => {
    if (activeTool !== 'erase-pipe-lasso' || loading) return;
    setDraft([...polygon]);
    setRemoved(finished ? pipesInLasso(network.pipes, polygon) : []);
  };
  const canValidate = activeTool === 'plant' ? draft.length === 1
    : activeTool === 'move-plant' ? !!selectedPlantId && draft.length === 1
    : activeTool === 'remove-plant' ? !!selectedPlantId
    : activeTool === 'draw-pipe' ? draft.length >= 2 : activeTool === 'erase-pipe-lasso' && removed.length > 0;
  const validate = () => {
    if (!canValidate || loading) return;
    setNetworks((prev) => {
      const current = prev[selectionId] ?? EMPTY_NETWORK;
      const next: Network = activeTool === 'plant'
        ? { ...current, plants: [...current.plants, { id: `plant-${crypto.randomUUID()}`, coordinates: draft[0] }] }
        : activeTool === 'move-plant'
          ? { ...current, plants: current.plants.map((p) => p.id === selectedPlantId ? { ...p, coordinates: draft[0] } : p) }
        : activeTool === 'remove-plant'
          ? { ...current, plants: current.plants.filter((p) => p.id !== selectedPlantId) }
        : activeTool === 'draw-pipe'
          ? { ...current, pipes: [...current.pipes, { id: `manual-${crypto.randomUUID()}`, kind: 'manual' as const, coordinates: [...draft] }] }
          : { ...current, pipes: current.pipes.filter((p) => !removed.includes(p.id)) };
      return { ...prev, [selectionId]: { ...next, stale: current.calculatedSignature !== undefined || current.stale } };
    });
    cancel();
  };
  const stale = !!network.stale || (network.calculatedSignature !== undefined && network.calculatedSignature !== targetSignature(targets));
  const precondition = !network.plants.length || !targets.length
    ? 'La sélection doit contenir au moins une centrale de production et au moins un bâtiment pour pouvoir générer un tracé optimal'
    : '';
  /** Dernier tracé optimal généré (persiste par sélection, comme la géométrie). */
  const trace = network.calculatedAt
    ? `Tracé optimal généré le ${new Date(network.calculatedAt).toLocaleDateString('fr-FR')} par ${network.calculatedBy}`
    : '';
  const calculate = () => {
    if (activeTool || loading || timer.current) return;
    if (precondition) { setMessage(precondition); return; }
    setLoading(true); setMessage('');
    timer.current = setTimeout(() => {
      setNetworks((prev) => ({ ...prev, [selectionId]: {
        ...simulateConnections(prev[selectionId] ?? EMPTY_NETWORK, targets),
        calculatedAt: new Date().toISOString(), calculatedBy: TRACE_AUTHOR,
      } }));
      timer.current = undefined;
      setLoading(false);
    }, 5000);
  };
  const calculateDensity = () => {
    if (activeTool || loading) return;
    density.calculate();
  };
  const instruction = activeTool === 'plant'
    ? 'Cliquez pour placer une nouvelle centrale, puis validez. Répétez pour en ajouter plusieurs.'
    : activeTool === 'move-plant'
      ? 'Glissez la centrale sur la carte, puis validez la nouvelle position. Annuler ou Échap restaure sa position.'
    : activeTool === 'remove-plant'
      ? selectedPlantId ? 'Une centrale sélectionnée pour suppression. Validez ou annulez.' : 'Cliquez la centrale à supprimer, puis validez.'
    : activeTool === 'draw-pipe'
      ? `${draft.length} point(s) — cliquez les sommets, puis « Terminer et valider » (2 minimum).`
      : activeTool === 'erase-pipe-lasso'
        ? `${removed.length} canalisation(s) à effacer — maintenez le bouton gauche et dessinez un lasso sur la carte. Toute ligne touchant la zone est ciblée. Relâchez, puis validez ou annulez.`
        : null;
  return { network, pipesVisible, setPipesVisible, activeTool, draft, removed, selectedPlantId, stale, loading, traceLoading,
    density: { ...density, calculate: calculateDensity }, message, precondition, trace, instruction,
    start, beginMove, cancel, pickPoint, pickLasso, pickPlant, canValidate, validate, calculate, length: networkLength(network.pipes) };
}
